import {
  Button,
  type ButtonProps,
  IconButton,
  type IconButtonProps,
} from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@hortis/ui/command";
import { Plus } from "@hortis/ui/icons";
import {
  Popover,
  PopoverContent,
  type PopoverContentProps,
  PopoverTrigger,
} from "@hortis/ui/popover";
import type { ReactElement, RefObject } from "react";
import { useRef, useState } from "react";
import type { OpenFilterArgs } from "../plant-materials/filters";
import type { LocationFilterType } from "./types";

const filters: Array<{ value: LocationFilterType; label: string }> = [
  {
    value: "geometry",
    label: "Polygon",
  },
];

interface AddLocationFilterProps {
  setOpenFilter: (args: OpenFilterArgs<LocationFilterType>) => void;
  side?: PopoverContentProps["side"];
  align?: PopoverContentProps["align"];
  buttonProps?: ButtonProps & {
    ref?: RefObject<HTMLButtonElement | null>;
    icon?: IconButtonProps["icon"];
  };
  iconButton?: boolean;
}

export const AddLocationFilter = ({
  setOpenFilter,
  buttonProps,
  iconButton,
  side,
  align = "start",
}: AddLocationFilterProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const menuAnchorEl = useRef(null);

  const handleSelect = (value: LocationFilterType) => {
    setOpenFilter({
      filterType: value,
      anchorElRef: buttonProps?.ref?.current ?? menuAnchorEl.current,
    });
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = (open: boolean) => {
    setOpen(open);
    if (open) {
      setOpenFilter({ filterType: false });
    }
  };

  return (
    <Popover open={open} onOpenChange={handleClose}>
      <PopoverTrigger asChild>
        {iconButton === true ? (
          <IconButton
            ref={menuAnchorEl}
            onClick={handleOpen}
            icon={Plus}
            ariaLabel="Add filter"
            size="sm"
            variant="tertiaryGray"
            className="border border-dashed border-grey-200"
            {...buttonProps}
          />
        ) : (
          <Button
            ref={menuAnchorEl}
            onClick={handleOpen}
            testId="add-filter-button"
            size="sm"
            startIcon={Plus}
            {...buttonProps}
          >
            {buttonProps?.children ?? "Add filter"}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent side={side} align={align} className="z-50">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandEmpty>No results</CommandEmpty>
          <CommandList>
            {filters.map((item) => (
              <CommandItem
                key={item.value}
                value={item.value}
                onSelect={() => {
                  handleSelect(item.value);
                }}
              >
                {item.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
